import type { ColumnDef } from '@gto/ui/data-table'

import type { AffiliateCommissionSchema } from '@/api/gen'
import type { DataTableProps } from '@/components/data-table'
import { DataTable } from '@/components/data-table'
import { useCommissionColumns } from '@/pages/commissions/use-commission-columns'

/**
 * Generic over the row so the admin orders view can pass its richer
 * `AffiliateAdminOrderRowSchema` and prepend a partner column typed to it — the old
 * wrapper needed an `as GridColDef` cast there because non-generic `DataGridProProps`
 * erased the row type.
 */
type CommissionsTableProps<R extends AffiliateCommissionSchema> = {
  showCustomerEmail?: boolean
  /** Extra columns rendered BEFORE the shared commission columns — the admin
   *  orders view prepends the owning partner here. */
  leadingColumns?: ColumnDef<R>[]
} & Omit<DataTableProps<R>, 'columns' | 'getRowClassName'>

// A refunded commission is heavily dimmed; a locked one less so, since it is still
// pending rather than void.
const rowClassName = (row: AffiliateCommissionSchema): string | undefined => {
  if (row.status === 'REFUNDED') {
    return 'opacity-45'
  }
  if (row.status === 'LOCKED') {
    return 'opacity-70'
  }

  return undefined
}

export const CommissionsTable = <R extends AffiliateCommissionSchema>({
  showCustomerEmail,
  leadingColumns,
  ...rest
}: CommissionsTableProps<R>) => {
  const commissionColumns = useCommissionColumns<R>({ showCustomerEmail })

  return (
    <DataTable<R>
      columns={[...(leadingColumns ?? []), ...commissionColumns]}
      getRowClassName={rowClassName}
      {...rest}
    />
  )
}
